import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Photo uploader for rapportini.
 * - props: paths (storage paths array), onChange(paths), rapportinoId (optional)
 * - max 10 images, 5 MB each
 */
export default function PhotoUploader({ paths = [], onChange, rapportinoId = null, max = 10 }) {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState({}); // path -> blobUrl

  // load auth token for img src query param
  const token = typeof window !== "undefined" ? localStorage.getItem("rapportini_token") : null;

  // load previews lazily once paths change
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const next = { ...previews };
      for (const p of paths) {
        if (next[p]) continue;
        try {
          const res = await api.get(`/files/${p}`, { responseType: "blob" });
          if (cancelled) return;
          next[p] = URL.createObjectURL(res.data);
        } catch {
          /* ignore single failures */
        }
      }
      if (!cancelled) setPreviews(next);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [paths.join("|")]);

  // cleanup blob URLs on unmount
  useEffect(() => () => {
    Object.values(previews).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line
  }, []);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    if (paths.length + files.length > max) {
      toast.error(`Massimo ${max} foto per rapportino`);
      return;
    }
    setUploading(true);
    const newPaths = [...paths];
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name}: troppo grande (max 5 MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const url = rapportinoId
          ? `/uploads?rapportino_id=${rapportinoId}`
          : "/uploads";
        const res = await api.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(res.data.storage_path);
      }
      onChange?.(newPaths);
      toast.success("Foto caricate");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Caricamento non riuscito";
      toast.error(typeof msg === "string" ? msg : "Caricamento non riuscito");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = paths.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading || paths.length >= max}
          className="h-12"
          data-testid="photo-camera-button"
        >
          <Camera className="h-4 w-4 mr-2" /> Scatta foto
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || paths.length >= max}
          className="h-12"
          data-testid="photo-upload-button"
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Upload className="h-4 w-4 mr-2" />
          )}
          Da galleria
        </Button>
        <span className="text-xs text-slate-500 self-center">
          {paths.length}/{max} · max 5 MB · jpg/png/webp/heic
        </span>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
      </div>

      {paths.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {paths.map((p, idx) => (
            <div
              key={p}
              className="relative aspect-square rounded-md overflow-hidden border border-slate-200 bg-slate-100"
              data-testid={`photo-thumb-${idx}`}
            >
              {previews[p] ? (
                <img
                  src={previews[p]}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-xs text-slate-400">
                  caricamento…
                </div>
              )}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-white grid place-items-center hover:bg-red-600"
                data-testid={`photo-remove-${idx}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper used by print page for direct img URL with auth query param
export function fileUrlWithAuth(path) {
  const token = typeof window !== "undefined" ? localStorage.getItem("rapportini_token") : null;
  const t = token ? `?auth=${encodeURIComponent(token)}` : "";
  return `${BACKEND_URL}/api/files/${path}${t}`;
}
